import { publicAsset } from '@/components/navigation/navigationAssets'
import type { VotePriorityResult } from '@/types/votePriority'
import { useState } from 'react'

const resultIcons = [
  ['seguridad', 'icons/election/seguridad.svg', '#1267d9'],
  ['movilidad', 'icons/election/transporte.svg', '#5aa0f2'],
  ['transporte', 'icons/election/transporte.svg', '#5aa0f2'],
  ['empleo', 'icons/election/trabajo.svg', '#f5b800'],
  ['trabajo', 'icons/election/trabajo.svg', '#f5b800'],
  ['espacio', 'icons/election/espacio-publico.svg', '#159447'],
  ['corrupcion', 'icons/election/corrupción.svg', '#e63946'],
  ['corrupción', 'icons/election/corrupción.svg', '#e63946'],
  ['servicio', 'icons/election/servicios-basicos.svg', '#1267d9'],
  ['obra', 'icons/election/obras-publicas.svg', '#f5b800'],
]

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

interface CityOpinionProps {
  results: VotePriorityResult[]
  totalResponses?: number
  status?: 'loading' | 'ready' | 'error'
  variant?: 'default' | 'candidates'
  collapsibleResults?: boolean
  collapsedLimit?: number
}

function getResultMeta(item: VotePriorityResult) {
  const searchable = normalize(`${item.id} ${item.label}`)
  const match = resultIcons.find(([keyword]) => searchable.includes(normalize(keyword)))

  return {
    icon: publicAsset(match?.[1] ?? 'icons/election/ciudad.svg'),
    color: match?.[2] ?? '#1267d9',
  }
}

function getResponseLabel(totalResponses = 0) {
  return `${totalResponses} ${totalResponses === 1 ? 'persona ha respondido' : 'personas han respondido'}`
}

export function CityOpinion({
  results,
  totalResponses = 0,
  status = 'ready',
  variant = 'default',
  collapsibleResults = false,
  collapsedLimit = 3,
}: CityOpinionProps) {
  const [showAllResults, setShowAllResults] = useState(false)
  const sortedResults = [...results].sort((a, b) => {
    if (b.votes !== a.votes) return b.votes - a.votes
    if (b.percentage !== a.percentage) return b.percentage - a.percentage
    return a.label.localeCompare(b.label, 'es')
  }).slice(0, 6)
  const visibleResults = collapsibleResults && !showAllResults
    ? sortedResults.slice(0, collapsedLimit)
    : sortedResults
  const hasHiddenResults = collapsibleResults && sortedResults.length > collapsedLimit

  return (
    <section className="city-opinion-card" aria-labelledby="city-opinion-title">
      <header>
        <h2 id="city-opinion-title">Tu ciudad opina</h2>
        {variant === 'candidates' ? (
          <p>Así votaron las personas sobre los temas que deberían atenderse con mayor prioridad dentro de tu cantón.</p>
        ) : (
          <p>
            <span>Estas son las acciones que las personas atenderían según la encuesta</span>
            <span className="city-opinion-card__separator" aria-hidden="true">·</span>
            <b>{getResponseLabel(totalResponses)}</b>
          </p>
        )}
      </header>

      {status === 'loading' && !sortedResults.length ? (
        <p className="city-opinion-card__empty" role="status">Cargando resultados...</p>
      ) : null}

      {status === 'error' && !sortedResults.length ? (
        <p className="city-opinion-card__empty" role="status">No se pudieron cargar los resultados.</p>
      ) : null}

      {status === 'ready' && !sortedResults.length ? (
        <p className="city-opinion-card__empty">Aún no hay resultados.</p>
      ) : null}

      {sortedResults.length ? (
        <div className="city-opinion-card__bars">
          {visibleResults.map((item) => {
            const percentage = item.percentage * 100
            const meta = getResultMeta(item)

            return (
              <div className="city-opinion-row" key={item.id}>
                <span className="city-opinion-row__icon">
                  <img src={meta.icon} alt="" loading="lazy" decoding="async" />
                </span>
                <strong>{item.label}</strong>
                <span
                  className="city-opinion-row__track"
                  role="meter"
                  aria-label={`${item.label}: ${percentage.toFixed(1)}%`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Number(percentage.toFixed(1))}
                >
                  <i
                    style={{
                      backgroundColor: meta.color,
                      width: `${Math.min(percentage, 100)}%`,
                    }}
                  />
                </span>
                <b>{variant === 'candidates' ? percentage.toFixed(0) : percentage.toFixed(1)}%</b>
              </div>
            )
          })}
        </div>
      ) : null}

      {hasHiddenResults ? (
        <button
          className="city-opinion-card__toggle"
          type="button"
          aria-expanded={showAllResults}
          onClick={() => setShowAllResults((current) => !current)}
        >
          {showAllResults ? 'Ver solo 3 prioridades' : 'Ver todos los resultados'}
        </button>
      ) : null}
    </section>
  )
}
